import React, { useState } from 'react';
import {
  Box,
  Tooltip,
  CircularProgress,
  useTheme,
  IconButton
} from '@mui/material';
import FiltersSidebar from '../components/vehicles/FiltersSidebar';
import VehicleOperations from '../components/vehicles/VehicleOperations';
import GenerateCsvModal from '../components/csv/GenerateCsvModal';
import SyncIcon from '@mui/icons-material/Sync';
import useMediaQuery from '@mui/material/useMediaQuery';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RoleBasedAccess from '../services/RoleBasedAccess';
import PaginationFooter from '../components/vehicles/PaginationFooter';
import { useVehicles } from '../hooks/vehicles/useVehicles';
import { VehiclesContext } from '../context/VehiclesContext';
import { defaultFilters } from '../components/vehicles/config';
import VehicleCardList from '../components/vehicles/VehicleCardList';
import VehicleDesktopTable from '../components/vehicles/VehicleDesktopTable';
import AddAssetModal from '../components/vehicles/AddAssetModal/ModalContainer'

const Vehicles: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const vehiclesState = useVehicles();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState(false);

  const filtersApplied =
    Object.keys(vehiclesState.filters).length > 0 &&
    JSON.stringify(vehiclesState.filters) !== JSON.stringify(defaultFilters);

  const sidebarWidth = isSidebarOpen
    ? (isMobile ? '70vw' : '280px')
    : (isMobile ? '12vw' : '60px');

  return (
    <VehiclesContext.Provider value={vehiclesState}>
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box
          sx={{
            height: '100%',
            overflowY: 'auto',
            width: sidebarWidth,
            transition: 'width 0.3s ease-in-out',
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.text.primary,
            display: 'flex',
            flexDirection: 'column',
            px: isSidebarOpen ? 2 : 0,
            zIndex: 100,
            flexShrink: 0
          }}
        >
          {isSidebarOpen ? (
            <FiltersSidebar
              toggleSidebar={() => setIsSidebarOpen(false)}
            />
          ) : (
            <Box display="flex" flexDirection="column">
              <Tooltip title="Open Filters" placement="right" arrow>
                <IconButton
                  onClick={() => setIsSidebarOpen(true)}
                  sx={{
                    m: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 5,
                    color: filtersApplied ? '#fff' : theme.palette.text.primary,
                    backgroundColor: filtersApplied ? 'darkred' : 'transparent',
                    '&:hover': {
                      backgroundColor: filtersApplied ? '#a30000' : theme.palette.action.hover,
                      borderColor: filtersApplied ? '#a30000' : theme.palette.text.primary,
                    },
                  }}
                >
                  <FilterAltOutlinedIcon />
                </IconButton>
              </Tooltip>


              <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor']}>
                <Tooltip title="Add New Asset" placement="right" arrow>
                  <IconButton
                    onClick={() => setIsAddAssetModalOpen(true)}
                    sx={{
                      m: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 5,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        borderColor: theme.palette.text.primary,
                      },
                    }}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              </RoleBasedAccess>

              <GenerateCsvModal />

              <Tooltip title="Poll for new vehicles every 60 seconds" placement="right" arrow>
                <IconButton
                  onClick={() => setPollingEnabled((prev) => !prev)}
                  sx={{
                    m: 1,
                    borderRadius: 5,
                    border: `1px solid ${theme.palette.divider}`,
                    color: pollingEnabled ? '#fff' : theme.palette.text.primary,
                    backgroundColor: pollingEnabled ? 'darkred' : 'transparent',
                    '&:hover': {
                      backgroundColor: pollingEnabled ? '#a30000' : theme.palette.action.hover,
                      borderColor: pollingEnabled ? '#a30000' : theme.palette.text.primary,
                    },
                  }}
                >
                  {pollingEnabled ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SyncIcon />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: `calc(100vw - ${sidebarWidth})`,
            padding: 1,
            color: 'text.primary',
            overflow: 'hidden',
          }}
        >

          {vehiclesState.loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '50vh',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                paddingBottom: '20px',
                scrollbarWidth: 'none',        // Firefox
                '&::-webkit-scrollbar': {
                  display: 'none',             // Chrome, Safari
                },
              }}>
                <RoleBasedAccess allowedRoles={['Admin', 'Support', 'Auditor']}>
                  <VehicleOperations />
                </RoleBasedAccess>
                {isMobile ? <VehicleCardList /> : <VehicleDesktopTable />}
              </Box>

              {/* Pagination + Results Per Page */}
              <PaginationFooter />
            </>
          )}
        </Box>

        {/* Modal is rendered here */}
        <AddAssetModal
          open={isAddAssetModalOpen}
          onClose={() => setIsAddAssetModalOpen(false)}
        />
      </Box>
    </VehiclesContext.Provider >
  );
};

export default Vehicles;
